import { ListMenuItem, newToast } from "@compass/core";
import { LkPersonTypeId, Permission, PermissionAction, TenantInfo } from "generated/openApi";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setAppToast } from "redux/reducers/appSlice";
import { isAuthorized } from "services";
import { StudentBulkUpdatePageState, studentBulkUpdatePageRoute } from "../StudentBulkUpdatePage/StudentBulkUpdatePage";
import {
  StudentBulkAssignGroupPageState,
  studentBulkAssignGroupPageRoute,
} from "../StudentBulkAssignGroupPage/StudentBulkAssignGroupPage";
import {
  StudentBulkCreateServicesPageState,
  studentBulkCreateServicesPageRoute,
} from "../StudentBulkCreateServicesPage/StudentBulkCreateServicesPage";
import { studentBulkEmailPageRoute, StudentBulkEmailPageState } from "../StudentBulkEmailPage/StudentBulkEmailPage";
import { studentBulkMergePageRoute, StudentBulkMergePageState } from "../StudentBulkMergePage/StudentBulkMergePage";
import {
  createTextingConversationsPageRoute,
  CreateTextingConversationsPageState,
} from "pages/texting/CreateTextingConversationsPage/CreateTextingConversationsPage";
import ApiService from "services/apiService";

const MAX_BULK_WALLET_PASS_RECIPIENTS = 10000;

export interface WalletPassResults {
  sent: number;
  total: number;
  errorMessages: string[];
}

export const useStudentListMenuItems = (props: {
  tenantTextingInfo: TenantInfo | undefined;
  permission: Permission;
  selectedIds: number[];
  clearSelectedIds?: () => void;
  showWalletPassResults?: (results: WalletPassResults) => void;
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const menuItems: ListMenuItem[] = [];

  if (isAuthorized(props.permission, PermissionAction.Update))
    menuItems.push({
      label: "Update",
      action: (selectedIds: number[]) =>
        navigate(studentBulkUpdatePageRoute.getPath(), {
          state: { selectedIds: selectedIds } as StudentBulkUpdatePageState,
        }),
    });

  if (isAuthorized(Permission.StudentGroup, PermissionAction.Create))
    menuItems.push({
      label: "Assign Group",
      action: (selectedIds: number[]) =>
        navigate(studentBulkAssignGroupPageRoute.getPath(), {
          state: { selectedIds: selectedIds } as StudentBulkAssignGroupPageState,
        }),
    });

  if (isAuthorized(Permission.StudentService, PermissionAction.Create))
    menuItems.push({
      label: "+ New Services",
      action: (selectedIds: number[]) =>
        navigate(studentBulkCreateServicesPageRoute.getPath(), {
          state: { selectedIds: selectedIds } as StudentBulkCreateServicesPageState,
        }),
    });

  if (isAuthorized(props.permission, PermissionAction.Update))
    menuItems.push({
      label: "Send Emails",
      action: (selectedIds: number[]) =>
        navigate(studentBulkEmailPageRoute.getPath(), {
          state: { selectedIds: selectedIds } as StudentBulkEmailPageState,
        }),
    });

  // Scope this action to the active students table and exclude it from the deleted-students table.
  if (props.clearSelectedIds && isAuthorized(Permission.WalletPass, PermissionAction.Create))
    menuItems.push({
      label: "Email Wallet Passes",
      action: (selectedIds: number[]) => {
        if (selectedIds.length > MAX_BULK_WALLET_PASS_RECIPIENTS) {
          dispatch(
            setAppToast(
              newToast(
                "error",
                `A maximum of ${MAX_BULK_WALLET_PASS_RECIPIENTS} students can be sent Wallet Passes at one time. You have ${selectedIds.length} selected.`
              )
            )
          );
          return;
        }

        if (window.confirm(`Email an Apple Wallet pass to ${selectedIds.length} selected student(s)?`)) {
          ApiService.walletPass
            .emailBulkWalletPass(selectedIds)
            .then((response) => {
              const errorMessages = response.errorMessages || [];

              if (errorMessages.length === 0) {
                dispatch(
                  setAppToast(newToast("success", `Wallet Passes emailed to ${response.sent} student(s).`))
                );
              } else {
                props.showWalletPassResults?.({
                  sent: response.sent,
                  total: response.total,
                  errorMessages: errorMessages,
                });
                dispatch(
                  setAppToast(
                    newToast(
                      response.sent > 0 ? "warning" : "error",
                      response.sent > 0
                        ? `Sent ${response.sent} of ${response.total}. ${errorMessages.length} failed.`
                        : `No Wallet Passes were sent. ${errorMessages.length} failed.`
                    )
                  )
                );
                console.warn("Wallet Pass email errors:", errorMessages);
              }

              if (response.sent > 0) props.clearSelectedIds?.();
            })
            .catch(() => {
              dispatch(setAppToast(newToast("error", "Failed to email Wallet Passes.")));
            });
        }
      },
    });

  if (isAuthorized(props.permission, PermissionAction.Update)) {
    menuItems.push({
      label: "Merge",
      action: (selectedIds: number[]) =>
        navigate(studentBulkMergePageRoute.getPath(), {
          state: { selectedIds: selectedIds } as StudentBulkMergePageState,
        }),
      disabled: props.selectedIds.length < 2,
    });
  }

  if (props.tenantTextingInfo && props.tenantTextingInfo.canInfobip && isAuthorized(Permission.Texting, PermissionAction.Create)) {
    menuItems.push({
      label: "+ Texting",
      action: (selectedIds: number[]) =>
        navigate(createTextingConversationsPageRoute.getPath(), {
          state: {
            personTypeId: Number(LkPersonTypeId.Student),
            personIds: selectedIds,
          } as CreateTextingConversationsPageState,
        }),
    });
  }

  return menuItems;
};
